import React from 'react'
import { useTranslation } from 'react-i18next'
import { FaTimes } from 'react-icons/fa'
import { TicketStatusBadge } from '../TicketStatusCard'
import { TicketStatus } from '../TicketStatusCard/TicketStatusBadge.types'
import { useCurrentEvent, EventType } from '../../hooks/useCurrentEvent'
import { useThemeAssets } from '../../hooks/useThemeAssets'
import styles from '../TicketStatusCard/TicketActionButton.module.css'

export interface TicketInfoCardProps {
  listTitle: string
  listDate?: string
  ticketNumber: string | number
  betDate: string
  amountOfBet: number
  winAmount: number
  status: TicketStatus
  canCancel?: boolean
  onCancel?: () => void
  onClickCheckResult?: () => void
}

interface CancelButtonProps {
  onClick?: () => void
  label: string
}

const CancelButton: React.FC<CancelButtonProps> = ({ onClick, label }) => (
  <button
    type="button"
    onClick={onClick}
    className={`inline-flex items-center gap-1.5 px-4 py-1.5 text-sm font-semibold ${styles.actionBtn} bg-[var(--color-error-500)] text-[var(--color-primary-50)] shadow-[0_2px_0.5px_0_rgba(0,0,0,0.25)] hover:bg-[var(--color-error-600)] active:bg-[var(--color-error-700)] transition-colors duration-150 cursor-pointer`}
  >
    <FaTimes className="text-base" aria-hidden="true" />
    {label}
  </button>
)

const TicketInfoCard: React.FC<TicketInfoCardProps> = ({
  listTitle,
  listDate,
  ticketNumber,
  betDate,
  amountOfBet,
  winAmount,
  status,
  canCancel = false,
  onCancel,
  onClickCheckResult,
}) => {
  const { t } = useTranslation()
  const event = useCurrentEvent()
  const { svgAssets } = useThemeAssets()

  // Check if Chinese New Year event is active
  const isChineseNewYear = event === EventType.CHINESE_NEW_YEAR
  const isSakura = event === EventType.SAKURA
  const isHalloween = event === EventType.HALLOWEEN
  const abstractBorderImage = (svgAssets as { ABSTRACT_BORDER?: string | null })
    ?.ABSTRACT_BORDER
  const webLeftImage = (svgAssets as { WEB_LEFT?: string | null })?.WEB_LEFT
  const webRightImage = (svgAssets as { WEB_RIGHT?: string | null })?.WEB_RIGHT

  return (
    <div
      className={`w-full shadow-md bg-[var(--ticket-info-card-bg)] border-t-4 border-[var(--ticket-info-card-top-border)] rounded-2xl p-5 sm:p-8 relative overflow-visible ${isChineseNewYear ? 'cny-ticket-card' : ''} ${isSakura ? 'sakura-ticket-card' : ''} ${isHalloween ? 'halloween-ticket-card' : ''}`}
    >
      {/* Event Abstract Border - 4 corners (CNY/Sakura) */}
      {(isChineseNewYear || isSakura) && abstractBorderImage && (
        <>
          <img
            src={abstractBorderImage}
            alt=""
            className="cny-ticket-border cny-ticket-border-tl"
          />
          <img
            src={abstractBorderImage}
            alt=""
            className="cny-ticket-border cny-ticket-border-tr"
          />
          <img
            src={abstractBorderImage}
            alt=""
            className="cny-ticket-border cny-ticket-border-bl"
          />
          <img
            src={abstractBorderImage}
            alt=""
            className="cny-ticket-border cny-ticket-border-br"
          />
        </>
      )}
      {/* Halloween Web Decorations - Top 2 corners only */}
      {isHalloween && webLeftImage && webRightImage && (
        <>
          <img
            src={webLeftImage}
            alt=""
            className="cny-ticket-border cny-ticket-border-tl"
          />
          <img
            src={webRightImage}
            alt=""
            className="cny-ticket-border cny-ticket-border-tr"
          />
        </>
      )}
      {/* Top Section: List, Ticket Number, Bet Date */}
      <div className="flex flex-col sm:grid sm:grid-cols-3 gap-3 sm:gap-4 mb-3 sm:mb-4">
        {/* List */}
        <div className="flex flex-col gap-1">
          <span className="text-xs text-[var(--ticket-info-label)]">
            {t('pollItem.list')}
          </span>
          <div className="flex items-baseline gap-2">
            <span
              className={`text-lg sm:text-2xl font-bold text-[var(--ticket-info-value)] ${
                onClickCheckResult
                  ? 'cursor-pointer hover:opacity-80 transition-opacity'
                  : ''
              }`}
              onClick={onClickCheckResult}
            >
              {listTitle}
            </span>
            {listDate && (
              <span className="text-xs text-[var(--ticket-info-label)]">
                ({listDate})
              </span>
            )}
          </div>
        </div>

        {/* Ticket Number & Bet Date Row */}
        <div className="flex items-start justify-between sm:contents">
          {/* Ticket Number */}
          <div className="flex flex-col gap-1 sm:items-center">
            <span className="text-xs text-[var(--ticket-info-label)]">
              {t('pollItem.pollNumber')}
            </span>
            <span className="text-base sm:text-2xl font-bold text-[var(--ticket-info-value)]">
              {ticketNumber}
            </span>
          </div>

          {/* Bet Date */}
          <div className="flex flex-col gap-1 items-end">
            <span className="text-xs text-[var(--ticket-info-label)]">
              {t('pollItem.betDate')}
            </span>
            <span className="text-sm sm:text-base text-[var(--ticket-info-label)]">
              {betDate}
            </span>
          </div>
        </div>
      </div>

      <hr className="border-t border-[var(--ticket-info-divider)] opacity-30 my-3 sm:my-4" />

      {/* Middle Section: Amount of Bet & Win Amount */}
      <div className="flex items-start justify-between sm:items-center mb-3 sm:mb-0">
        {/* Amount of Bet & Win Amount - spread on mobile, grouped on desktop */}
        <div className="flex items-start justify-between w-full sm:w-1/2 sm:justify-start sm:items-center sm:gap-10">
          {/* Amount of Bet */}
          <div className="flex flex-col gap-1">
            <span className="text-xs text-[var(--ticket-info-label)]">
              {t('pollItem.betAmount')}
            </span>
            <span className="text-lg sm:text-2xl font-bold text-[var(--ticket-info-amount)]">
              {amountOfBet.toLocaleString('en-US', {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              })}
            </span>
          </div>

          {/* Win Amount */}
          <div className="flex flex-col gap-1 items-end sm:items-start">
            <span className="text-xs text-[var(--ticket-info-label)]">
              {t('pollItem.winAmount')}
            </span>
            <span className="text-lg sm:text-2xl font-bold text-[var(--ticket-info-amount)]">
              {winAmount.toLocaleString('en-US', {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              })}
            </span>
          </div>
        </div>

        {/* Vertical Divider - Desktop only */}
        <div className="hidden sm:block h-16 w-px bg-[var(--ticket-info-vertical-divider)] opacity-30" />

        {/* Right: Status & Cancel - Desktop only */}
        <div className="hidden sm:flex sm:w-1/2 items-center justify-end gap-10">
          {/* Status */}
          <div className="flex flex-row gap-3 items-center">
            <span className="text-xs text-[var(--ticket-info-label)]">
              {t('pollItem.status')}
            </span>
            <TicketStatusBadge status={status} />
          </div>

          {/* Cancel Bet */}
          {canCancel && (
            <div className="flex flex-row gap-3 items-center">
              <span className="text-xs text-[var(--ticket-info-label)]">
                {t('pollItem.cancelBet')}
              </span>
              <CancelButton onClick={onCancel} label={t('pollItem.cancel')} />
            </div>
          )}
        </div>
      </div>

      {/* Mobile Divider & Status/Cancel Row */}
      <div className="sm:hidden">
        <hr className="border-t border-[var(--ticket-info-divider)] opacity-30 my-3" />

        {/* Status | Cancel Bet */}
        <div className="flex items-center justify-between">
          {/* Status */}
          <div className="flex items-center gap-2">
            <span className="text-xs text-[var(--ticket-info-label)]">
              {t('pollItem.status')}
            </span>
            <TicketStatusBadge status={status} />
          </div>

          {/* Cancel Bet */}
          {canCancel && (
            <div className="flex items-center gap-2">
              <span className="text-xs text-[var(--ticket-info-label)]">
                {t('pollItem.cancelBet')}
              </span>
              <CancelButton onClick={onCancel} label={t('pollItem.cancel')} />
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export default TicketInfoCard
